import moment from "moment";
import { IN_DRAFT, LISTING_STATE_DRAFT, LISTING_STATE_PUBLISHED, NO_MATCH, PARTIAL_MATCH, PERFECT_MATCH, PUBLISH_WITH_LESS_48, SUBSCRIPTION_TYPE_TIER_1, SUBSCRIPTION_TYPE_TIER_2, SUBSCRIPTION_TYPE_TIER_3 } from "./types";
import { ACTIVE_TIER, BID_MADE, BUSINESS_CATEGORIES, BUSINESS_CHILD_CATEGORIES, BUSINESS_NAME, BUSINESS_START_YEAR, BUSINESS_SUB_CATEGORIES, COMPANY_LOCATION, NUMBER_OF_FEET, NUMBER_OF_PROJECTS, OPERATIONAL_STATES, OWNER_COMPANY, OWNER_EMAIL, PROFILE_URL, PROJECT_BUDGET, PROJECT_CHILD_CATEGORIES, PROJECT_PRIME_CATEGORIES, PROJECT_STATES, PROJECT_SUB_CATEGORIES, PUBLISHED_AT, SIZE_OF_BUSINESS, USER_ID, WEBSITE } from "./pipeDriveKeys";

export const formatDate = (date) => {
  if (!date) return "Invalid Date";

  // Ensure it's a valid Moment object (Safari-safe)
  const mDate = moment(new Date(date));

  if (!mDate.isValid()) {
    return "Invalid Date";
  }

  return mDate.format("MMM DD, YYYY");
};

export const token_allowance_checks = (userSubscriptions = []) => {
  const planDetail = Array.isArray(userSubscriptions)
    ? userSubscriptions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
    : null;

  if (!planDetail || !planDetail.current_period_end) {
    return false;
  }

  const { current_period_end } = planDetail;
  const endDate = moment.unix(current_period_end); // convert timestamp to moment
  const daysUntilEnd = endDate.diff(moment(), 'days');

  // If current period has ended (or ends today), return false
  if (daysUntilEnd < 0) {
    return false;
  } else {
    return true;
  }
};

export const getMatchType = (listing, currentUser) => {
  const listingStates = listing?.listingState?.map(e => e.label) || [];
  const listingCategories = listing?.businessCategory || [];

  const userStates = currentUser?.attributes?.profile?.publicData?.operational_states?.map(e => e.label) || [];
  const userCategories = currentUser?.attributes?.profile?.publicData?.businessCategory?.map(e => e.label) || [];

  // ✅ Check if all user states exist in listing states
  const statesMatch = userStates.every(state => listingStates.includes(state));

  // Check categories
  const matchedCategories = listingCategories.filter(cat => userCategories.includes(cat));
  const allCategoriesMatch = matchedCategories.length === listingCategories.length;
  const someCategoriesMatch = matchedCategories.length > 0;

  if (!statesMatch) return NO_MATCH;
  if (statesMatch && allCategoriesMatch) return PERFECT_MATCH;
  if (statesMatch && someCategoriesMatch) return PARTIAL_MATCH;

  return NO_MATCH;
};

export const formattedBudget = (businessSubCategory = [], businessSubCategoryData = {}) => {
  return businessSubCategory
    .map(subcat => {
      const key = subcat.option;
      const data = businessSubCategoryData[key];
      if (!data) return null;

      return {
        subcategory: subcat.label,
        budget: data.price || 0
      };
    })
    .filter(Boolean); // This removes nulls from the result
};

export const PLANS = {
  'tier_1': {
    plan: SUBSCRIPTION_TYPE_TIER_1,
    businessProfile: true,
    receiveQuotesLimit: 3,
    createQuotesLimit: 3,
    tokenAllowance: 2,
    discoverableInSearch: true,
    basicReviewSystem: true,
    inAppMessaging: true,
    professionalEndorsements: true,
    freeTrialQuotes: true,
    priorityAdvancedMatchingHours: 0,
    earnTokensPerJob: 0,
    endorsementRequests: 2,
    creditsCaps: 5
  },
  "tier_2": {
    plan: SUBSCRIPTION_TYPE_TIER_2,
    businessProfile: true,
    receiveQuotesLimit: 10,
    createQuotesLimit: 10,
    tokenAllowance: 10,
    discoverableInSearch: true,
    basicReviewSystem: true,
    inAppMessaging: true,
    professionalEndorsements: true,
    freeTrialQuotes: true,
    priorityAdvancedMatchingHours: 0,
    earnTokensPerJob: 0,
    endorsementRequests: 5,
    creditsCaps: 10
  },
  "tier_3": {
    plan: SUBSCRIPTION_TYPE_TIER_3,
    businessProfile: true,
    receiveQuotesLimit: "Infinity",
    createQuotesLimit: "Infinity",
    tokenAllowance: 30,
    discoverableInSearch: true,
    basicReviewSystem: true,
    inAppMessaging: true,
    professionalEndorsements: true,
    freeTrialQuotes: false,
    priorityAdvancedMatchingHours: 10,
    earnTokensPerJob: 3,
    endorsementRequests: "Infinity",
    creditsCaps: 25
  },
};

export const checkPlanFeature = (userPlanTier, featureKey) => {
  const plan = PLANS[userPlanTier];

  if (!plan) {
    throw new Error(`Invalid plan tier: ${userPlanTier}`);
  }

  return plan ?? null;
};

export const isLatestMilestoneInPast = (latestMilestone) => {
  if (!latestMilestone) return false;

  const latestDate = new Date(latestMilestone?.milestoneDate);
  const now = new Date();

  if (latestDate < now) {
    return true;
  } else {
    return false;
  };
};

export const applyPlanUpgradeToUserData = (currentUserData, newTier) => {
  const currentTier = currentUserData.plan;
  const currentPlan = PLANS[currentTier];
  const newPlan = PLANS[newTier];

  if (!currentPlan || !newPlan) {
    throw new Error(`Invalid plan tier(s): current=${currentTier}, new=${newTier}`);
  }

  const featureKeysToDelta = ['createQuotesLimit', 'receiveQuotesLimit', 'tokenAllowance'];

  const updatedUserData = {
    ...currentUserData,
    plan: newTier, // Update plan to new tier
  };

  featureKeysToDelta.forEach((key) => {
    const currentPlanLimit = currentPlan[key];
    const newPlanLimit = newPlan[key];
    const userCurrentValue = currentUserData[key];

    if (
      typeof currentPlanLimit === 'number' &&
      typeof newPlanLimit === 'number' &&
      isFinite(currentPlanLimit) &&
      isFinite(newPlanLimit)
    ) {
      const delta = newPlanLimit - currentPlanLimit;
      updatedUserData[key] = userCurrentValue + delta;
    } else if (newPlanLimit === "Infinity") {
      updatedUserData[key] = "Infinity";
    } else {
      updatedUserData[key] = newPlanLimit;
    }
  });

  return updatedUserData;
};

export const calculateAverage = reviews => {
  if (reviews?.length === 0) {
    return 0;
  }
  const sum =
    reviews &&
    reviews?.reduce((accumulator, currentValue) => accumulator + currentValue.attributes.rating, 0);
  const average = sum / reviews?.length;
  return average;
};

/**
 * Builds the payload for creating or updating a Pipedrive person
 * @param {Object} currentUser
 * @returns {Object} payload
 */
export const buildPipedrivePayload = (currentUser) => {
  const { email, profile } = (currentUser && currentUser.id && currentUser.attributes) || {};
  const { firstName, lastName, publicData = {}, protectedData = {} } = profile || {};
  const {
    phoneNumber,
    size_of_business,
    company_name,
    website,
    companyLocation,
    businessCategory = [],
    businessStartYear,
    businessSubCategory = [],
    businessSubChildCategory = [],
    operational_states = [],
    projectsCount = 0
  } = publicData;

  const { selectedFeatures = {} } = protectedData;
  const { plan } = selectedFeatures;

  const profileUrl = `${process.env.REACT_APP_MARKETPLACE_ROOT_URL}/u/${currentUser?.id?.uuid}`;

  return {
    'name': `${firstName || ''} ${lastName || ''}`.trim(),
    "first_name": `${firstName || ''}`.trim(),
    "last_name": `${lastName || ''}`.trim(),
    "email": email,
    'phone': phoneNumber,
    [BUSINESS_START_YEAR]: businessStartYear,
    [OPERATIONAL_STATES]: operational_states?.map(e => e.label).join(', '),
    [SIZE_OF_BUSINESS]: size_of_business,
    [BUSINESS_CATEGORIES]: businessCategory?.map(e => e.label).join(', '),
    [BUSINESS_SUB_CATEGORIES]: businessSubCategory?.join(', '),
    [BUSINESS_CHILD_CATEGORIES]: businessSubChildCategory?.join(', '),
    [USER_ID]: currentUser?.id?.uuid,
    [PROFILE_URL]: profileUrl,
    [NUMBER_OF_PROJECTS]: projectsCount,
    [ACTIVE_TIER]: plan,
    [COMPANY_LOCATION]: companyLocation?.search,
    [WEBSITE]: website,
    [BUSINESS_NAME]: company_name
  };
};

export const buildPayload = ({ response, params, getState }) => {
  const { title, publicData, state } = params?.attributes || {};
  const stageName = (state == LISTING_STATE_DRAFT) ? IN_DRAFT : PUBLISH_WITH_LESS_48;
  const stage_id = response.find(e => e.name === stageName)?.id;
  const {
    businessCategory = [],
    businessSubCategoriesForBudget = [],
    businessSubChildCategory = [],
    listingState = [],
    businessSubCategoryData = {},
    businessSubCategory = []
  } = publicData || {};

  const { currentUser } = getState().user;
  const publishDate = (state == LISTING_STATE_PUBLISHED) ? new Date() : null;
  const bidMade = (state == LISTING_STATE_PUBLISHED) ? "false" : null;
  const subCategories = businessSubCategoriesForBudget?.length > 0 ?
    businessSubCategoriesForBudget.flat().map(item => item.label).join(',')
    : businessSubCategory.map(e => e.label).join(',');

  const payload = {
    title,
    stage_id,
    [OWNER_EMAIL]: currentUser?.attributes?.email,
    [OWNER_COMPANY]: currentUser?.attributes?.profile?.publicData?.company_name,
    [PROJECT_STATES]: listingState.map(e => e.label).join(','),
    [PROJECT_PRIME_CATEGORIES]: businessCategory.map(e => e.label).join(','),
    [PROJECT_SUB_CATEGORIES]: subCategories,
    [PROJECT_CHILD_CATEGORIES]: businessSubChildCategory.map(e => e.label).join(','),
    [NUMBER_OF_FEET]: Object.values(businessSubCategoryData).reduce((sum, item) => {
      return sum + Number(item.totalDistance || 0);
    }, 0),
    [PROJECT_BUDGET]: Object.values(businessSubCategoryData).map(item => String(item.price)).join(','),
    [PUBLISHED_AT]: publishDate,
    [BID_MADE]: bidMade,
  };
  return payload;
};
